import { Calendar } from "lucide-react";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { DayPicker } from "react-day-picker";
import { useFormContext } from "react-hook-form";

import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/primitives/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/primitives/popover";
import useTranslations from "@/shared/lang/useTranslations";

import {
  DATE_TRIGGER_CLASS,
  DAY_PICKER_CLASSNAMES,
  DAY_PICKER_STYLE,
  dateToIso,
  isoToDate,
  resolveDayPickerLocale,
} from "./colocaDayPicker.shared";
import { DayPickerChevron } from "./DayPickerChevron";
import { DayPickerDropdown } from "./DayPickerDropdown";

export type ColocaDateFieldProps = {
  /** Field name as registered in the parent `useForm` schema. Must match the Zod key exactly. */
  name: string;
  /** Visible label rendered above the trigger. */
  label?: ReactNode;
  /** Helper text rendered below the trigger. */
  description?: ReactNode;
  /** Placeholder when no date is selected. Falls back to a localized "Pick a date". */
  placeholder?: string;
  /** Latest selectable date (inclusive). */
  maxDate?: Date;
  /** Earliest selectable date (inclusive). */
  minDate?: Date;
  /** Earliest year shown in the year dropdown. Defaults to 1900. */
  fromYear?: number;
  /** Latest year shown in the year dropdown. Defaults to current year. */
  toYear?: number;
  disabled?: boolean;
};

/**
 * Date picker field wired to the nearest React Hook Form context.
 *
 * The form value is an ISO `YYYY-MM-DD` string so it stays compatible with backend schemas that already
 * accept this shape. The trigger displays the date formatted in the user's locale.
 */
export function ColocaDateField({
  name,
  label,
  description,
  placeholder,
  maxDate,
  minDate,
  fromYear = 1900,
  toYear,
  disabled,
}: ColocaDateFieldProps) {
  const { control } = useFormContext();
  const { t, activeLanguage } = useTranslations("global");
  const [open, setOpen] = useState(false);
  const dpLocale = useMemo(() => resolveDayPickerLocale(activeLanguage), [activeLanguage]);

  const resolvedPlaceholder = placeholder ?? t("input_date_placeholder");

  const startMonth = useMemo(() => new Date(fromYear, 0, 1), [fromYear]);
  const endMonth = useMemo(() => {
    const year = toYear ?? new Date().getFullYear();
    return new Date(year, 11, 31);
  }, [toYear]);

  const formatDisplay = useCallback(
    (date: Date) =>
      new Intl.DateTimeFormat(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date),
    [],
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedDate = isoToDate(field.value as string | undefined);
        const displayValue = selectedDate ? formatDisplay(selectedDate) : "";

        return (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger
                render={
                  <button
                    type="button"
                    disabled={disabled}
                    aria-haspopup="dialog"
                    onBlur={field.onBlur}
                    className={DATE_TRIGGER_CLASS}
                  >
                    <span className={displayValue ? "" : "text-muted-foreground"}>
                      {displayValue || resolvedPlaceholder}
                    </span>
                    <Calendar size={16} className="text-muted-foreground" />
                  </button>
                }
              />
              <PopoverContent align="start" className="w-auto p-4">
                <DayPicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      field.onChange(dateToIso(date));
                      setOpen(false);
                    } else {
                      field.onChange("");
                    }
                  }}
                  captionLayout="dropdown"
                  navLayout="after"
                  reverseYears
                  startMonth={startMonth}
                  endMonth={endMonth}
                  defaultMonth={selectedDate ?? endMonth}
                  disabled={[
                    ...(minDate ? [{ before: minDate }] : []),
                    ...(maxDate ? [{ after: maxDate }] : []),
                  ]}
                  showOutsideDays
                  locale={dpLocale}
                  style={DAY_PICKER_STYLE}
                  components={{ Chevron: DayPickerChevron, Dropdown: DayPickerDropdown }}
                  classNames={DAY_PICKER_CLASSNAMES}
                />
              </PopoverContent>
            </Popover>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
